import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  Grid,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { Filter, FilterGroup, Statement } from 'src/types/myFollowUp';
import useSurveySettings from 'src/hooks/useSurveySettings';
import { numberOrDash, signedNumberOrDash } from 'src/helper/helper';
import InlineErrorMessage from 'src/components/common/InlineErrorMessage';
import useFollowUpDetail from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';

import { styles } from './index.styles';

interface KpiSelectionProps {
  kpis?: FilterGroup;
  selectedKpi?: Filter;
  onKpiSelection: (kpi: Filter) => void;
  selectedStatement?: Statement;
  onStatementSelection: (statement: Statement) => void;
  disabled?: boolean;
}

const KpiSelection: FC<KpiSelectionProps> = ({
  kpis,
  selectedKpi,
  onKpiSelection,
  selectedStatement,
  onStatementSelection,
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('myFollowUp');
  const { minRespondentAmount } = useSurveySettings();

  const { mutationError } = useFollowUpDetail();

  if (!kpis) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ my: 1 }}>
        <Typography variant="body1">{kpis.title}</Typography>
        {kpis.isNegsel && (
          <InlineErrorMessage
            error={t('kpiSelection.negselError', { minRespondentAmount })}
          />
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {kpis.filters.map((kpi) => (
            <Grid item xs={12} sm={6} md={4} key={kpi.id}>
              <Button
                disabled={disabled}
                sx={styles.kpiButton(
                  theme,
                  !!selectedKpi && kpi.id === selectedKpi.id,
                )}
                fullWidth
                onClick={() => onKpiSelection(kpi)}
              >
                <Typography title={kpi.label} variant="h6" sx={styles.kpiLabel}>
                  {kpi.label}
                </Typography>
                <Box sx={styles.indicatorContainer}>
                  <Typography variant="body3">
                    {numberOrDash(kpi.indicator1, true)}
                  </Typography>
                  {kpi.indicator2 !== null && (
                    <Typography
                      variant="body3"
                      sx={styles.trendIndicator(theme, kpi.indicator2)}
                    >
                      {signedNumberOrDash(kpi.indicator2)}
                    </Typography>
                  )}
                </Box>
              </Button>
            </Grid>
          ))}
        </Grid>
        <InlineErrorMessage error={mutationError?.kpi} />
      </Box>
      {selectedKpi && (
        <Box sx={styles.statementsContainer}>
          <Divider sx={styles.divider} />
          <Box>
            <Typography variant="body3">
              {t('kpiSelection.selectKpiStatement')}
            </Typography>
          </Box>

          <Box sx={styles.tableHeader}>
            <Typography variant="caption" sx={styles.headerText}>
              {t('kpiSelection.statement')}
            </Typography>
            <Typography variant="caption" sx={styles.headerText}>
              {t('kpiSelection.favorable')}
            </Typography>
            <Typography variant="caption" sx={styles.headerText}>
              {t('kpiSelection.change')}
            </Typography>
          </Box>

          {selectedKpi.statements.map((statement) => (
            <Box
              key={statement.id}
              sx={styles.tableRow(
                theme,
                !!selectedStatement && statement.id === selectedStatement.id,
              )}
              onClick={
                disabled ? undefined : () => onStatementSelection(statement)
              }
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  ...styles.statementContainer,
                }}
              >
                <Radio
                  disabled={disabled}
                  checked={
                    !!selectedStatement && statement.id === selectedStatement.id
                  }
                  onChange={() => onStatementSelection(statement)}
                  sx={styles.radio(
                    theme,
                    !!selectedStatement &&
                      statement.id === selectedStatement.id,
                  )}
                />
                <Typography
                  title={statement.label}
                  variant="caption2"
                  sx={styles.statementLabel}
                >
                  {statement.label}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={styles.indicatorText}>
                {numberOrDash(statement.indicator1, true)}
              </Typography>
              <Typography variant="caption" sx={styles.indicatorText}>
                {signedNumberOrDash(statement.indicator2)}
              </Typography>
            </Box>
          ))}
          <InlineErrorMessage error={mutationError?.statement} />
        </Box>
      )}
    </Box>
  );
};

export default KpiSelection;
